import React, { useState, useRef, useEffect } from "react";
import logo from "../assets/images/logo.png";
import cancel from "../assets/images/navbar/cancel.png";
import arrow from "../assets/images/order/orange-arrow.png";
import { authService } from "../services";

type OtpModalProps = {
  isOpen: boolean;
  onClose: () => void;
  mobileNumber: string;
  onVerify?: () => void;
};

const Otp: React.FC<OtpModalProps> = ({ isOpen, onClose, mobileNumber, onVerify }) => {
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(27);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer
  useEffect(() => {
    if (!isOpen || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, countdown]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setOtp(["", "", "", "", "", ""]);
      setError("");
      setCountdown(27);
      setTimeout(() => inputRefs.current[0]?.focus(), 0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Format countdown as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^[0-9]*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setError("");

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;

    const newOtp = ["", "", "", "", "", ""];
    pasted.split("").forEach((digit, i) => {
      newOtp[i] = digit;
    });
    setOtp(newOtp);
    setError("");
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      setError("Please enter the complete 6-digit OTP");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const phoneNumber = `+91${mobileNumber}`;
      const response = await authService.verifyOtp(phoneNumber, otpValue);
      
      // Store the token in localStorage
      if (response.data.token) {
        localStorage.setItem('authToken', response.data.token);
        console.log("Token stored successfully");
        
        // Call the onVerify callback
        if (onVerify) {
          onVerify();
        }
      }
    } catch (err: any) {
      console.error("OTP verification error:", err);
      setError(err.response?.data?.message || "Invalid OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    try {
      setError("");
      const phoneNumber = `+91${mobileNumber}`;
      const response: any = await authService.sendOtp(phoneNumber);
      setCountdown(30);
      setOtp(["", "", "", "", "", ""]);
      if (response?.data?.otp || response?.message) {
        const otpFromMsg = String(response?.data?.otp || "").slice(0, 6);
        if (otpFromMsg.length === 6) {
          setOtp(otpFromMsg.split(""));
        }
      }
      inputRefs.current[0]?.focus();
    } catch (err: any) {
      setError(err?.message || "Failed to resend OTP");
    }
  };

  return (
    <>
      {/* Backdrop Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-[1003] transition-colors duration-300"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center z-[1004] pointer-events-none">
        <div
          className="w-[545px] p-8 rounded-[24px] bg-white dark:bg-gray-800 pointer-events-auto relative transition-colors duration-300"
          style={{
            boxShadow: "-23px 23px 72px 0 rgba(0, 0, 0, 0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <div
            className="absolute top-6 right-6 h-[36px] w-[36px] cursor-pointer border dark:border-gray-600 rounded-full p-[7px] bg-white dark:bg-gray-700 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            onClick={onClose}
          >
            <img src={cancel} alt="Close" className="h-[18px] w-[18px]" />
          </div>

          {/* Logo */}
          <div className="mb-[36px]">
            <img src={logo} alt="Duddus" className="h-[54px] w-[166px]" />
          </div>

          {/* Title */}
          <h2 className="text-[#050505] dark:text-white font-urbanist text-[32px] font-semibold leading-[125%] mb-[8px]">
            Enter OTP
          </h2>

          {/* Subtitle */}
          <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[16px] font-medium leading-[140%] mb-[24px]">
            Enter 6 digit OTP sent to {mobileNumber}
          </p>

          {/* OTP Input Boxes */}
          <div className="flex justify-center gap-2 mb-[27px]">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                autoComplete="one-time-code"
                className="w-[50px] h-[50px] text-center text-[20px] font-semibold border-b-2 border-[#E0E0E0] dark:border-gray-600 rounded-[12px] outline-none focus:border-[#7402A7] dark:focus:border-purple-500 transition-colors shadow-[ 0 1px 4px 0 rgba(229, 229, 229, 0.40)] dark:shadow-none bg-[#FAFAFA] dark:bg-gray-700 dark:text-white"
              />
            ))}
          </div>
          <div className="text-center mb-[76px]">
            <p className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[16px] font-medium leading-[140%]">
              Didn't received OTP?{" "}
              {countdown > 0 ? (
                <>
                  Resend in{" "}
                  <span className="text-[#7604A5] dark:text-purple-400">
                    {formatTime(countdown)}
                  </span>
                </>
              ) : (
                <button
                  onClick={handleResend}
                  className="text-[#7604A5] dark:text-purple-400 font-semibold underline"
                >
                  Resend OTP
                </button>
              )}
            </p>
          </div>
          
          {/* Error Message */}
          {error && (
            <p className="text-red-500 font-urbanist text-[14px] mb-[16px] text-center">
              {error}
            </p>
          )}

          {/* Verify Button */}
          <button
            onClick={handleVerify}
            disabled={isLoading}
            className="w-full flex items-center justify-between py-4 px-6 bg-[#7402A7] dark:bg-purple-600 rounded-[150px] cursor-pointer hover:bg-[#5f0289] dark:hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-white font-urbanist text-[20px] font-semibold leading-[140%]">
              {isLoading ? "Verifying..." : "Verify OTP"}
            </span>
            <div className="h-[42px] w-[42px] flex justify-center items-center bg-white rounded-full">
              <img
                src={arrow}
                alt="right arrow"
                className="h-[41px] w-[30px] stroke-[2.587px] stroke-[#FB8606]"
              />
            </div>
          </button>
        </div>
      </div>
    </>
  );
};

export default Otp;
